import React, { useState, useRef, useEffect } from "react";
import { API_URLS } from "../../apiUrl";
import Pagination from '@mui/material/Pagination';
import SurveyReport from "../../popupModals/surveyReport";
import axiosInstance from "../../components/axiosInstance";

type FormData = {
    surveyorId: string;
    page: number;
    limit: number;
    municipality_id: string;
};

const SurveyorReport = ({ muniCipalityName, muniCipalityId }: any) => {
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [dataExistMessage, setDataExistMessage] = useState<string>("");
    const [failedMessage, setFailedMessage] = useState<string>("");
    const [tableData, setTableData] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [holdingId, setHoldingId] = useState<any>("");

    const handleShow = (holdingId: any) => {
        setShowModal(true);
        setHoldingId(holdingId);
    };

    const handleClose = () => setShowModal(false);

    const [inputData, setInputData] = useState({ page: 1, limit: 10 });
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage] = useState(10);

    const formRef = useRef<HTMLFormElement>(null);

    const [formData, setFormData] = useState<FormData>({
        surveyorId: "",
        page: 1,
        limit: 10,
        municipality_id: muniCipalityId
    });

    const [surveyor, setSurveyor] = useState<any[]>([]);

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: any = {};
        if (!formData.surveyorId.trim()) newErrors.surveyorId = "Surveyor is required";
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm()) {
            setLoading(true);
            try {
                const response = await axiosInstance.post(`${API_URLS.FetchSurveyData}`, formData);

                if (response.data.holding_info && response.data.holding_info.length > 0) {
                    setTableData(response.data.holding_info);
                    setTotalItems(response.data.total_count);
                    setDataExistMessage("Data successfully fetched.");
                    setTimeout(() => setDataExistMessage(""), 5000);
                } else {
                    setTableData([]);
                    setSuccessMessage("No results found for this surveyor.");
                    setTimeout(() => setSuccessMessage(""), 5000);
                }
            } catch (error) {
                setFailedMessage("Failed to fetch data.");
                setTimeout(() => setFailedMessage(""), 5000);
            } finally {
                setLoading(false);
            }
        }
    };


    const filteredData = tableData.filter((item: any) =>
        item.holding_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.holding_type?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handlePageChange = (event: any, value: number) => {
        setInputData((prevData) => ({
            ...prevData,
            page: value,
        }));
    };

    useEffect(() => {
        const fetchData = async () => {
            if (formData.surveyorId) {
                setLoading(true);
                try {
                    const response = await axiosInstance.post(`${API_URLS.FetchSurveyData}`, {
                        surveyorId: formData.surveyorId,
                        page: inputData.page,
                        limit: inputData.limit,
                        municipality_id: muniCipalityId
                    });

                    if (response.data.holding_info && response.data.holding_info.length > 0) {
                        setTableData(response.data.holding_info);
                        setTotalItems(response.data.total_count);
                    } else {
                        setTableData([]);
                        setTotalItems(0);
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, [inputData.page, formData.surveyorId, muniCipalityId]);

    useEffect(() => {
        const fetchSurveyors = async () => {
            try {
                const response = await axiosInstance.post(API_URLS.ListSurveyor, {
                    municipality_id: muniCipalityId,
                    page: 1,
                    limit: 300000,
                });
                setSurveyor(response.data.data.data);
            } catch (error) {
                console.error('Error fetching surveyors:', error);
            }
        };

        fetchSurveyors();
    }, [muniCipalityId]);

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const getStatusIcon = (status: number) => {
        switch (status) {
            case 1:
                return { icon: '✔', color: 'bg-green-500' };
            case 0:
                return { icon: '✘', color: 'bg-red-500' };
            case 3:
                return { icon: '⏳', color: 'bg-orange-500' };
            default:
                return { icon: '', color: '' };
        }
    };

    return (
        <div className="max-w-full mx-auto">
            <div className="flex flex-col gap-5">
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Surveyor Reports [{muniCipalityName}]</h1>

                <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
                    <form ref={formRef} onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <div>
                                <label
                                    htmlFor="surveyorId"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    Select Surveyor <span className="text-red-600">*</span>
                                </label>
                                <select
                                    id="surveyorId"
                                    name="surveyorId"
                                    value={formData.surveyorId}
                                    onChange={handleChange}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                >
                                    <option value="">Select Surveyor</option>
                                    {surveyor.map((item: any) => (
                                        <option key={item.surveyer_id} value={item.surveyer_id}>
                                            {item.survyer_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-end">
                                <button
                                    type="submit"
                                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Search
                                </button>
                            </div>
                        </div>
                    </form>

                    {successMessage && (
                        <div className="mt-4 p-4 bg-yellow-600 text-white rounded-lg flex justify-between items-center">
                            <span>{successMessage}</span>
                            <button
                                onClick={() => setSuccessMessage("")}
                                className="ml-4 text-white font-bold text-lg focus:outline-none"
                            >
                                &times;
                            </button>
                        </div>
                    )}

                    {dataExistMessage && (
                        <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
                            <span>{dataExistMessage}</span>
                            <button
                                onClick={() => setDataExistMessage("")}
                                className="ml-4 text-white font-bold text-lg focus:outline-none"
                            >
                                &times;
                            </button>
                        </div>
                    )}

                    {failedMessage && (
                        <div className="mt-4 p-4 bg-red-600 text-white rounded-lg flex justify-between items-center">
                            <span>{failedMessage}</span>
                            <button
                                onClick={() => setFailedMessage("")}
                                className="ml-4 text-white font-bold text-lg focus:outline-none"
                            >
                                &times;
                            </button>
                        </div>
                    )}

                    {filteredData.length > 0 && (
                        <div className="mt-6">
                            <div className="flex items-center gap-5 mb-4">
                                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Assigned Holdings List</h2>
                                <div className="flex items-center gap-3 ml-auto">
                                    <div className="flex gap-1 items-center">
                                        <i className="ri-check-line text-green-500"></i>
                                        <span className="text-sm">Completed</span>
                                    </div>
                                    <span className="text-gray-300">|</span>
                                    <div className="flex gap-1 items-center">
                                        <i className="ri-close-large-line text-red-500"></i>
                                        <span className="text-sm">Not Started</span>
                                    </div>
                                    <span className="text-gray-300">|</span>
                                    <div className="flex gap-1 items-center">
                                        <i className="ri-hourglass-fill text-orange-500"></i>
                                        <span className="text-sm">In Progress</span>
                                    </div>
                                </div>
                            </div>

                            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 dark:border-gray-800">
                                <table className="min-w-full table-auto">
                                    <thead className="bg-gray-100 border-b dark:border-gray-600 dark:bg-gray-700">
                                        <tr>
                                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Street Name</th>
                                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Name Of Complex</th>
                                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Holding Type</th>
                                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Holding No.</th>
                                            <th className="py-3 px-6 text-center text-sm font-medium text-gray-600 dark:text-gray-100">Survey Status</th>
                                            <th className="py-3 px-6 text-center text-sm font-medium text-gray-600 dark:text-gray-100">Survey Report</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={6} className="py-3 px-6 text-center">Loading...</td>
                                            </tr>
                                        ) : filteredData.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="py-3 px-6 text-center dark:bg-gray-800">No results found</td>
                                            </tr>
                                        ) : (
                                            filteredData.map((item: any, index: number) => {
                                                const status = getStatusIcon(item.is_survey_completed);
                                                return (
                                                    <tr key={index} className="border-b dark:border-gray-700 hover:bg-gray-50">
                                                        <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.street_name || 'N/A'}</td>
                                                        <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.name_of_complex_apartment || 'N/A'}</td>
                                                        <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.holding_type || 'N/A'}</td>
                                                        <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.holding_no || 'N/A'}</td>
                                                        <td className="py-3 px-6 text-center">
                                                            <span className={`inline-block text-white py-1 px-3 rounded-full ${status.color}`}>
                                                                {status.icon}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-6 text-center">
                                                            <button 
                                                                onClick={() => handleShow(item.holding_primary_id)}
                                                                className="text-blue-500 hover:text-blue-700"
                                                            >
                                                                <i className="ri-eye-fill"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {filteredData.length > 0 && (
                                <div className="flex justify-center mt-4">
                                    <Pagination
                                        count={totalPages}
                                        page={inputData.page}
                                        onChange={handlePageChange}
                                        color="primary"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <SurveyReport 
                showModal={showModal} 
                handleClose={handleClose} 
                surveyorId={formData.surveyorId} 
                municipalityId={muniCipalityId} 
                holdingId={holdingId} 
            />
        </div>
    );
};

export default SurveyorReport;